import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { CronogramaEditor, CronogramaView } from '@/components/CronogramaEditor';
import { gerarPDFEncontro, gerarPDFCelula } from '@/lib/pdfGenerator';
import { 
  ArrowLeft,
  Users, 
  MapPin, 
  Clock, 
  Target,
  Calendar,
  BookOpen,
  Plus,
  Trash2,
  Edit,
  UserPlus,
  UserMinus,
  Check,
  X,
  Flame,
  FileText,
  Download,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const roleLabels = {
  'lider': 'Líder',
  'co_lider': 'Co-líder',
  'pregador': 'Pregador',
  'secretario': 'Secretário(a)',
  'tesoureiro': 'Tesoureiro(a)',
  'membro': 'Membro',
  'admin_celula': 'Admin Célula'
};

export const CelulaDetalhesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    getCelulaById, 
    getCelulaMembros,
    loadCelulaMembros,
    getEncontros,
    loadEncontros,
    membros,
    updateCelula, 
    deleteCelula,
    createEncontro,
    addCelulaMembro,
    removeCelulaMembro,
    igrejaAtual
  } = useApp();

  const [celula, setCelula] = useState(null);
  const [celulaMembros, setCelulaMembros] = useState([]);
  const [encontros, setEncontros] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [encontroDialogOpen, setEncontroDialogOpen] = useState(false);
  const [membroDialogOpen, setMembroDialogOpen] = useState(false);
  const [selectedMembroId, setSelectedMembroId] = useState('');
  const [selectedRole, setSelectedRole] = useState('membro');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formCelula, setFormCelula] = useState({});
  const [novoEncontro, setNovoEncontro] = useState({
    data: '',
    horario: '',
    local: '',
    assuntos: '',
    livro: '',
    capitulo: '',
    pregador_id: '',
    observacoes: '',
    cronograma: [],
  });

  const [encontroDetalheDialogOpen, setEncontroDetalheDialogOpen] = useState(false);
  const [selectedEncontro, setSelectedEncontro] = useState(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!id || !igrejaAtual) return;
      
      setLoading(true);
      try {
        const celulaData = getCelulaById(id);
        if (celulaData) {
          setCelula(celulaData);
          setFormCelula({
            nome: celulaData.nome || '',
            descricao: celulaData.descricao || '',
            objetivo: celulaData.objetivo || '',
            principios: (celulaData.principios || []).join(', '),
            metas: (celulaData.metas || []).join(', '),
            endereco: celulaData.endereco || '',
            cidade: celulaData.cidade || '',
            dia_semana: celulaData.dia_semana || '',
            horario: celulaData.horario || '',
          });
          setNovoEncontro(prev => ({
            ...prev,
            horario: celulaData.horario || '',
            local: celulaData.endereco || '',
          }));
        }
        
        const membrosData = await loadCelulaMembros(id);
        setCelulaMembros(membrosData || []);
        
        const encontrosData = await loadEncontros(id);
        setEncontros(encontrosData || []);
      } catch (error) {
        console.error('Error loading celula data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, igrejaAtual]);

  // Handle edit celula
  const handleSaveCelula = async () => {
    if (!celula) return;
    
    setIsSubmitting(true);
    try {
      const updated = await updateCelula(celula.id, {
        ...formCelula,
        principios: formCelula.principios.split(',').map(p => p.trim()).filter(Boolean),
        metas: formCelula.metas.split(',').map(m => m.trim()).filter(Boolean),
      });
      setCelula(updated);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating celula:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete celula
  const handleDeleteCelula = async () => {
    if (!celula) return;
    
    try {
      await deleteCelula(celula.id);
      navigate('/celulas');
    } catch (error) {
      console.error('Error deleting celula:', error);
    }
  };

  // Handle create encontro
  const handleCreateEncontro = async () => {
    if (!novoEncontro.data) {
      toast.error('Selecione uma data para o encontro');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const encontroData = {
        data: novoEncontro.data,
        horario: novoEncontro.horario,
        local: novoEncontro.local,
        livro: novoEncontro.livro,
        capitulo: novoEncontro.capitulo,
        assuntos: novoEncontro.assuntos.split(',').map(a => a.trim()).filter(Boolean),
        pregador_id: novoEncontro.pregador_id || null,
        observacoes: novoEncontro.observacoes,
        cronograma: novoEncontro.cronograma,
      };
      
      const created = await createEncontro(celula.id, encontroData);
      setEncontros(prev => [...prev, created]);
      setEncontroDialogOpen(false);
      setNovoEncontro({
        data: '',
        horario: celula?.horario || '',
        local: celula?.endereco || '',
        assuntos: '',
        livro: '',
        capitulo: '',
        pregador_id: '',
        observacoes: '',
        cronograma: [],
      });
    } catch (error) {
      console.error('Error creating encontro:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle add membro
  const handleAddMembro = async () => {
    if (!selectedMembroId) return;
    
    setIsSubmitting(true);
    try {
      const newMembro = await addCelulaMembro(celula.id, selectedMembroId, selectedRole);
      setCelulaMembros(prev => [...prev, newMembro]);
      setMembroDialogOpen(false);
      setSelectedMembroId('');
      setSelectedRole('membro');
    } catch (error) {
      console.error('Error adding membro:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle remove membro
  const handleRemoveMembro = async (membroId) => {
    try {
      await removeCelulaMembro(celula.id, membroId);
      setCelulaMembros(prev => prev.filter(m => m.id !== membroId));
    } catch (error) {
      console.error('Error removing membro:', error);
    }
  };

  // Get available membros (not already in celula)
  const availableMembros = membros.filter(
    m => !celulaMembros.some(cm => cm.igreja_membro_id === m.id)
  );

  // PDF generation
  const handleGerarPDFCelula = () => {
    const pdfData = {
      ...celula,
      membros: celulaMembros,
      encontros: encontros,
    };
    gerarPDFCelula(pdfData);
    toast.success('PDF da célula gerado!');
  };

  const handleGerarPDFEncontro = (encontro) => {
    const pdfData = {
      ...encontro,
      celula: celula,
      membros: celulaMembros,
    };
    gerarPDFEncontro(pdfData, celula);
    toast.success('PDF do encontro gerado!');
  };

  if (loading) {
    return (
      <div className="page-transition">
        <div className="px-6 lg:px-8 py-8">
          <div className="max-w-5xl mx-auto">
            <Skeleton className="h-8 w-48 mb-8" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!celula) {
    return (
      <div className="page-transition">
        <div className="px-6 lg:px-8 py-12">
          <div className="max-w-md mx-auto text-center">
            <Flame className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Célula não encontrada</h2>
            <p className="text-muted-foreground mb-6">
              A célula que você está procurando não existe ou foi removida.
            </p>
            <Button asChild>
              <Link to="/celulas">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para Células
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-primary/10 via-background to-secondary/10 border-b border-border">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to="/celulas">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Link>
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary text-primary-foreground">
                <Flame className="w-8 h-8" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground">
                  {celula.nome}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                  {celula.dia_semana && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {celula.dia_semana}
                    </span>
                  )}
                  {celula.horario && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {celula.horario}
                    </span>
                  )}
                  {celula.cidade && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {celula.cidade}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleGerarPDFCelula}>
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              
              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Editar Célula</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label>Nome</Label>
                      <Input
                        value={formCelula.nome}
                        onChange={(e) => setFormCelula({...formCelula, nome: e.target.value})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Descrição</Label>
                      <Textarea
                        value={formCelula.descricao}
                        onChange={(e) => setFormCelula({...formCelula, descricao: e.target.value})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Objetivo</Label>
                      <Input
                        value={formCelula.objetivo}
                        onChange={(e) => setFormCelula({...formCelula, objetivo: e.target.value})}
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>Dia da Semana</Label>
                        <Input
                          value={formCelula.dia_semana}
                          onChange={(e) => setFormCelula({...formCelula, dia_semana: e.target.value})}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Horário</Label>
                        <Input
                          type="time"
                          value={formCelula.horario}
                          onChange={(e) => setFormCelula({...formCelula, horario: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>Endereço</Label>
                        <Input
                          value={formCelula.endereco}
                          onChange={(e) => setFormCelula({...formCelula, endereco: e.target.value})}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Cidade</Label>
                        <Input
                          value={formCelula.cidade}
                          onChange={(e) => setFormCelula({...formCelula, cidade: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label>Princípios (separados por vírgula)</Label>
                      <Input
                        value={formCelula.principios}
                        onChange={(e) => setFormCelula({...formCelula, principios: e.target.value})}
                        placeholder="Amor, Fé, Comunhão"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Metas (separadas por vírgula)</Label>
                      <Input
                        value={formCelula.metas}
                        onChange={(e) => setFormCelula({...formCelula, metas: e.target.value})}
                        placeholder="Alcançar 20 pessoas, 4 encontros mensais"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveCelula} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Salvar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir célula?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita. Todos os dados da célula serão removidos.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteCelula}>
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="info" className="space-y-6">
            <TabsList>
              <TabsTrigger value="info">Informações</TabsTrigger>
              <TabsTrigger value="membros">Membros ({celulaMembros.length})</TabsTrigger>
              <TabsTrigger value="encontros">Encontros ({encontros.length})</TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {celula.descricao && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Descrição</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{celula.descricao}</p>
                    </CardContent>
                  </Card>
                )}
                
                {celula.objetivo && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        Objetivo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{celula.objetivo}</p>
                    </CardContent>
                  </Card>
                )}
                
                {celula.principios && celula.principios.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Princípios</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {celula.principios.map((p, i) => (
                          <Badge key={i} variant="secondary">{p}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {celula.metas && celula.metas.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Metas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {celula.metas.map((m, i) => (
                          <li key={i} className="flex items-start gap-2 text-muted-foreground">
                            <Check className="w-4 h-4 text-green-500 mt-0.5" />
                            {m}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Membros Tab */}
            <TabsContent value="membros" className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Membros da Célula</h3>
                <Dialog open={membroDialogOpen} onOpenChange={setMembroDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Adicionar Membro
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Adicionar Membro</DialogTitle>
                      <DialogDescription>
                        Selecione um membro da igreja para adicionar à célula
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label>Membro</Label>
                        <Select value={selectedMembroId} onValueChange={setSelectedMembroId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um membro" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMembros.map(m => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.user_nome || m.user_email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Papel</Label>
                        <Select value={selectedRole} onValueChange={setSelectedRole}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(roleLabels).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setMembroDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleAddMembro} disabled={!selectedMembroId || isSubmitting}>
                        {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Adicionar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {celulaMembros.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Nenhum membro nesta célula</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {celulaMembros.map((membro) => (
                    <Card key={membro.id} className="card-elevated">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {membro.user_nome?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{membro.user_nome || 'Sem nome'}</p>
                            <p className="text-xs text-muted-foreground truncate">{membro.user_email}</p>
                            <Badge variant="outline" className="mt-1 text-xs">
                              {roleLabels[membro.role] || membro.role}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveMembro(membro.id)}
                          >
                            <UserMinus className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Encontros Tab */}
            <TabsContent value="encontros" className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Encontros</h3>
                <Dialog open={encontroDialogOpen} onOpenChange={setEncontroDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Novo Encontro
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Agendar Encontro</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label>Data *</Label>
                          <Input
                            type="date"
                            value={novoEncontro.data}
                            onChange={(e) => setNovoEncontro({...novoEncontro, data: e.target.value})}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label>Horário</Label>
                          <Input
                            type="time"
                            value={novoEncontro.horario}
                            onChange={(e) => setNovoEncontro({...novoEncontro, horario: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label>Local</Label>
                        <Input
                          value={novoEncontro.local}
                          onChange={(e) => setNovoEncontro({...novoEncontro, local: e.target.value})}
                        />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label>Livro</Label>
                          <Input
                            value={novoEncontro.livro}
                            onChange={(e) => setNovoEncontro({...novoEncontro, livro: e.target.value})}
                            placeholder="Ex: João"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label>Capítulo</Label>
                          <Input
                            value={novoEncontro.capitulo}
                            onChange={(e) => setNovoEncontro({...novoEncontro, capitulo: e.target.value})}
                            placeholder="Ex: 15"
                          />
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label>Assuntos (separados por vírgula)</Label>
                        <Input
                          value={novoEncontro.assuntos}
                          onChange={(e) => setNovoEncontro({...novoEncontro, assuntos: e.target.value})}
                          placeholder="Tema 1, Tema 2, Tema 3"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Pregador</Label>
                        <Select
                          value={novoEncontro.pregador_id}
                          onValueChange={(value) => setNovoEncontro({...novoEncontro, pregador_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o pregador" />
                          </SelectTrigger>
                          <SelectContent>
                            {celulaMembros.map(m => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.user_nome || m.user_email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Observações</Label>
                        <Textarea
                          value={novoEncontro.observacoes}
                          onChange={(e) => setNovoEncontro({...novoEncontro, observacoes: e.target.value})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEncontroDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleCreateEncontro} disabled={!novoEncontro.data || isSubmitting}>
                        {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Agendar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {encontros.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Nenhum encontro agendado</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {encontros
                    .sort((a, b) => new Date(b.data) - new Date(a.data))
                    .map((encontro) => {
                      const isPast = new Date(encontro.data) < new Date();
                      const pregador = celulaMembros.find(m => m.id === encontro.pregador_id);
                      
                      return (
                        <Card key={encontro.id} className="card-elevated">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-4">
                                <div className={`p-2 rounded-lg ${isPast ? 'bg-muted' : 'bg-primary/10'}`}>
                                  <BookOpen className={`w-5 h-5 ${isPast ? 'text-muted-foreground' : 'text-primary'}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">
                                      {format(new Date(encontro.data), "EEEE, d 'de' MMMM", { locale: ptBR })}
                                    </p>
                                    <Badge variant={encontro.status === 'realizado' ? 'secondary' : encontro.status === 'cancelado' ? 'destructive' : 'default'}>
                                      {encontro.status === 'realizado' ? 'Realizado' : encontro.status === 'cancelado' ? 'Cancelado' : 'Agendado'}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {encontro.horario}
                                    </span>
                                    {encontro.local && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {encontro.local}
                                      </span>
                                    )}
                                  </div>
                                  {encontro.livro && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      📖 {encontro.livro} {encontro.capitulo}
                                    </p>
                                  )}
                                  {pregador && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      🎤 {pregador.user_nome}
                                    </p>
                                  )}
                                  {encontro.status === 'realizado' && (
                                    <div className="flex items-center gap-4 mt-2 text-sm">
                                      <span className="text-green-600">
                                        <Check className="w-3 h-3 inline mr-1" />
                                        {encontro.presentes?.length || 0} presentes
                                      </span>
                                      <span className="text-red-500">
                                        <X className="w-3 h-3 inline mr-1" />
                                        {encontro.ausentes?.length || 0} ausentes
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGerarPDFEncontro(encontro)}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                PDF
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
